import {Injectable} from '@angular/core';
import {combineLatest, fromEvent, Subject, takeUntil} from 'rxjs';
import {debounceTime, startWith, tap} from 'rxjs/operators';
import {BsModalService} from 'ngx-bootstrap/modal';
import {LocalStorageService} from '@services/local-storage.service';
import {LOGOUT_TIMEOUT, LOGOUT_TIMER_TIMEOUT} from './constants';
import {LogoutTimerComponent} from './auto-logout.component';

@Injectable({
  providedIn: 'root',
})
export class AutoLogoutService {
  listenersUnsubscribed: boolean;
  stopListeners: Subject<void> = new Subject<void>();

  constructor(
    private modalService: BsModalService,
    private localStorageService: LocalStorageService,
  ) {
  }

  private _initSubscriptions() {
    this.listenersUnsubscribed = false;
    combineLatest(
      ['mousemove', 'mousedown', 'keyup', 'wheel', 'touchend'].map(
        (event) => fromEvent(document.body, event).pipe(startWith(null))
      ),
    )
    .pipe(
      takeUntil(this.stopListeners),
      debounceTime(10000),
      tap(() => this.localStorageService.setLastEventTime()),
      debounceTime(LOGOUT_TIMER_TIMEOUT),
      tap(() => {
        this.stopListeners.next();
        this.listenersUnsubscribed = true;
        const openTime = new Date().getTime();
        const lastEventTime = this.localStorageService.getLastEventTime();
        this.modalService.show(LogoutTimerComponent, {
          ignoreBackdropClick: true,
          initialState: {
            // because timer should start from available time
            logoutAfter: LOGOUT_TIMEOUT - (openTime - lastEventTime - LOGOUT_TIMER_TIMEOUT),
          },
        });
      }),
    ).subscribe();
  }

  start() {
    // a remembered session lasts 7 days on the backend, idle logout would defeat it
    if (this.localStorageService.getCurrentUserDetails()?.rememberMe) {
      return;
    }
    if (this.listenersUnsubscribed === undefined) {
      this._initSubscriptions();
    }
  }

  reset() {
    if (this.listenersUnsubscribed) {
      this._initSubscriptions();
    }
  }

  stop() {
    this.stopListeners.next();
  }
}
